/**
 * "Add to calendar" for a decided plan. Offers a universal .ics download (Apple /
 * Outlook / Google) and a one-tap Google Calendar link. Uses a floating local
 * time (no timezone) so it lands on the attendee's own clock.
 */
const DURATION_HOURS = 2;

const pad = (n: number) => String(n).padStart(2, "0");

/** "yyyy-MM-ddTHH:mm:ss" (local/floating) -> compact "yyyyMMddTHHmmss", plus the +2h end. */
function range(startsAt: string): { start: string; end: string } | null {
  const m = startsAt.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? "0"));
  const fmt = (x: Date) =>
    `${x.getFullYear()}${pad(x.getMonth() + 1)}${pad(x.getDate())}T${pad(x.getHours())}${pad(x.getMinutes())}${pad(x.getSeconds())}`;
  const end = new Date(dt.getTime() + DURATION_HOURS * 3600 * 1000);
  return { start: fmt(dt), end: fmt(end) };
}

const AddToCalendar: React.FC<{ title: string; startsAt?: string | null; location?: string | null; url?: string | null }> = ({
  title,
  startsAt,
  location,
  url,
}) => {
  if (!startsAt) return null;
  const r = range(startsAt);
  if (!r) return null;

  const details = url ?? "";
  const loc = location ?? "";

  const download = () => {
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Kinoes//EN",
      "BEGIN:VEVENT",
      `DTSTART:${r.start}`,
      `DTEND:${r.end}`,
      `SUMMARY:${title.replace(/\n/g, " ")}`,
      loc ? `LOCATION:${loc.replace(/\n/g, " ")}` : "",
      details ? `DESCRIPTION:${details.replace(/\n/g, " ")}` : "",
      "END:VEVENT",
      "END:VCALENDAR",
    ]
      .filter(Boolean)
      .join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = "kinoes-plan.ics";
    a.click();
    URL.revokeObjectURL(href);
  };

  const google =
    `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}` +
    `&dates=${r.start}/${r.end}` +
    (details ? `&details=${encodeURIComponent(details)}` : "") +
    (loc ? `&location=${encodeURIComponent(loc)}` : "");

  return (
    <div className="mt-3 flex items-center justify-center gap-4 text-sm">
      <button onClick={download} className="font-medium text-white/90 underline hover:text-white">
        📅 Add to calendar
      </button>
      <a
        href={google}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-white/70 underline hover:text-white"
      >
        Google
      </a>
    </div>
  );
};

export default AddToCalendar;
